import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  Handle,
  Position,
  type Node,
  type Edge,
  type NodeProps,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Avatar, Box, Card, CircularProgress, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { useApi } from 'api/ApiProvider';
import { HRApiWrapper } from 'api/HRApiWrapper';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import { handleAPIError } from 'common/errorHandler';
import Api from 'axiosApi/api';

// ─── Constants ───────────────────────────────────────────────────────────────

const NODE_W = 180;
const NODE_H = 120;
const H_GAP = 40;
const V_GAP = 70;
const MAX_INITIAL_DEPTH = 4; // show 5 levels (0–4) without expand

// ─── Types ───────────────────────────────────────────────────────────────────

interface ApiNode {
  id: number;
  name: string;
  lastName: string;
  supervisorId: number | null;
  isActive: boolean;
  profiles: string[];
}

interface TreeNode extends ApiNode {
  children: TreeNode[];
}

interface OrgNodeData extends ApiNode {
  depth: number;
  hasHiddenChildren: boolean;
  childCount: number;
  isExpanded: boolean;
  [key: string]: unknown;
}

// ─── Context ─────────────────────────────────────────────────────────────────

interface OrgChartCtx {
  hrApi: HRApiWrapper;
  toggleExpand: (id: number) => void;
}

const OrgChartContext = createContext<OrgChartCtx | null>(null);

// ─── Layout helpers ───────────────────────────────────────────────────────────

function getSubtreeWidth(node: TreeNode, depth: number, expanded: Set<number>): number {
  const showChildren = depth < MAX_INITIAL_DEPTH || expanded.has(node.id);
  if (!showChildren || node.children.length === 0) return NODE_W + H_GAP;
  return node.children.reduce(
    (sum, child) => sum + getSubtreeWidth(child, depth + 1, expanded),
    0,
  );
}

function buildFlowData(
  roots: TreeNode[],
  expanded: Set<number>,
  toggleExpand: (id: number) => void,
): { nodes: Node[]; edges: Edge[] } {
  const rfNodes: Node[] = [];
  const rfEdges: Edge[] = [];

  function place(siblings: TreeNode[], depth: number, parentId: number | null, startX: number): void {
    let currentX = startX;
    for (const node of siblings) {
      const subtreeW = getSubtreeWidth(node, depth, expanded);
      const nodeX = currentX + subtreeW / 2 - NODE_W / 2;
      const nodeY = depth * (NODE_H + V_GAP);

      const showChildren = depth < MAX_INITIAL_DEPTH || expanded.has(node.id);
      const hasHiddenChildren = !showChildren && node.children.length > 0;

      rfNodes.push({
        id: `node-${node.id}`,
        type: 'orgNode',
        position: { x: nodeX, y: nodeY },
        data: {
          id: node.id,
          name: node.name,
          lastName: node.lastName,
          supervisorId: node.supervisorId,
          isActive: node.isActive,
          profiles: node.profiles,
          depth,
          hasHiddenChildren,
          childCount: node.children.length,
          isExpanded: expanded.has(node.id),
        } satisfies OrgNodeData,
      });

      if (parentId !== null) {
        rfEdges.push({
          id: `edge-${parentId}-${node.id}`,
          source: `node-${parentId}`,
          target: `node-${node.id}`,
          type: 'smoothstep',
          style: { stroke: '#bbb', strokeWidth: 1.5 },
        });
      }

      if (showChildren && node.children.length > 0) {
        place(node.children, depth + 1, node.id, currentX);
      }

      currentX += subtreeW;
    }
  }

  place(roots, 0, null, 0);
  return { nodes: rfNodes, edges: rfEdges };
}

// ─── Custom Node Component ────────────────────────────────────────────────────

function OrgChartNode({ data }: NodeProps) {
  const nodeData = data as OrgNodeData;
  const ctx = useContext(OrgChartContext);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!ctx) return;
    ctx.hrApi
      .getEmployeePictureUrl('1', nodeData.id)
      .then((res: any) => {
        const url = res?.data?.data ?? res?.data ?? null;
        if (url && typeof url === 'string' && url.startsWith('http')) {
          setPhotoUrl(url);
        }
      })
      .catch(() => {});
  }, [nodeData.id, ctx]);

  const initials = `${nodeData.name?.[0] ?? ''}${nodeData.lastName?.[0] ?? ''}`.toUpperCase();

  const avatarColor = useMemo(() => {
    const colors = ['#1976d2', '#388e3c', '#7b1fa2', '#c62828', '#e65100', '#0288d1', '#00695c'];
    return colors[(nodeData.id ?? 0) % colors.length];
  }, [nodeData.id]);

  return (
    <Box
      sx={{
        width: NODE_W,
        minHeight: NODE_H,
        bgcolor: nodeData.isActive ? '#fff' : '#f5f5f5',
        border: '1px solid',
        borderColor: nodeData.isActive ? 'primary.light' : 'grey.300',
        borderRadius: 2,
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        p: 1.5,
        gap: 0.5,
        position: 'relative',
        cursor: 'default',
      }}
    >
      <Handle type="target" position={Position.Top} style={{ opacity: 0 }} />

      <Avatar
        src={photoUrl ?? undefined}
        alt={`${nodeData.name} ${nodeData.lastName}`}
        sx={{ width: 44, height: 44, bgcolor: avatarColor, fontSize: 16, fontWeight: 700 }}
      >
        {!photoUrl && initials}
      </Avatar>

      <Tooltip title={`${nodeData.name} ${nodeData.lastName}`} placement="top">
        <Typography
          variant="body2"
          fontWeight={600}
          textAlign="center"
          sx={{
            lineHeight: 1.2,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: NODE_W - 24,
          }}
        >
          {nodeData.name} {nodeData.lastName}
        </Typography>
      </Tooltip>

      {nodeData.profiles.length > 0 && (
        <Tooltip title={nodeData.profiles.join(' · ')} placement="bottom">
          <Typography
            variant="caption"
            color="text.secondary"
            textAlign="center"
            sx={{
              lineHeight: 1.2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: NODE_W - 24,
            }}
          >
            {nodeData.profiles.join(' · ')}
          </Typography>
        </Tooltip>
      )}

      {nodeData.hasHiddenChildren && (
        <Box
          onClick={() => ctx?.toggleExpand(nodeData.id)}
          sx={{
            mt: 0.5,
            width: 24,
            height: 24,
            borderRadius: '50%',
            bgcolor: 'primary.main',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            fontSize: 12,
            fontWeight: 700,
            '&:hover': { bgcolor: 'primary.dark' },
          }}
          title={`Expand ${nodeData.childCount} report${nodeData.childCount !== 1 ? 's' : ''}`}
        >
          <AddIcon sx={{ fontSize: 16 }} />
        </Box>
      )}

      {nodeData.isExpanded && nodeData.depth >= MAX_INITIAL_DEPTH && (
        <Box
          onClick={() => ctx?.toggleExpand(nodeData.id)}
          sx={{
            mt: 0.5,
            width: 24,
            height: 24,
            borderRadius: '50%',
            bgcolor: 'grey.400',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            '&:hover': { bgcolor: 'grey.600' },
          }}
          title="Collapse"
        >
          <RemoveIcon sx={{ fontSize: 16 }} />
        </Box>
      )}

      <Handle type="source" position={Position.Bottom} style={{ opacity: 0 }} />
    </Box>
  );
}

const nodeTypes = { orgNode: OrgChartNode };

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function OrgChartPage() {
  const api: Api = useApi();
  const hrApi = useMemo(() => new HRApiWrapper(api.employeeApi), [api.employeeApi]);
  const toast = useToastMessageQueue();

  const [apiNodes, setApiNodes] = useState<ApiNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedNodes, setExpandedNodes] = useState<Set<number>>(new Set());

  useEffect(() => {
    setLoading(true);
    hrApi
      .getOrgChartData()
      .then((res: any) => {
        const data: ApiNode[] = res?.data?.data ?? res?.data ?? [];
        setApiNodes(data);
      })
      .catch((e: any) => handleAPIError(e, toast, {}))
      .finally(() => setLoading(false));
  }, [hrApi]);

  const toggleExpand = useCallback((id: number) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const ctx = useMemo<OrgChartCtx>(() => ({ hrApi, toggleExpand }), [hrApi, toggleExpand]);

  // Build tree from flat list
  const roots = useMemo<TreeNode[]>(() => {
    if (apiNodes.length === 0) return [];

    const map = new Map<number, TreeNode>();
    for (const n of apiNodes) {
      map.set(n.id, { ...n, children: [] });
    }

    const roots: TreeNode[] = [];
    for (const n of apiNodes) {
      const node = map.get(n.id)!;
      if (n.supervisorId && map.has(n.supervisorId)) {
        map.get(n.supervisorId)!.children.push(node);
      } else {
        roots.push(node);
      }
    }

    // Sort children alphabetically at each level
    function sortTree(nodes: TreeNode[]) {
      nodes.sort((a, b) => a.lastName.localeCompare(b.lastName) || a.name.localeCompare(b.name));
      for (const n of nodes) sortTree(n.children);
    }
    sortTree(roots);

    return roots;
  }, [apiNodes]);

  const { nodes, edges } = useMemo(
    () => buildFlowData(roots, expandedNodes, toggleExpand),
    [roots, expandedNodes, toggleExpand],
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (roots.length === 0) {
    return (
      <Card sx={{ m: 2, p: 4 }}>
        <Typography color="text.secondary" textAlign="center">
          No employees found.
        </Typography>
      </Card>
    );
  }

  return (
    <OrgChartContext.Provider value={ctx}>
      <Card sx={{ m: 2 }}>
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography color="primary" variant="h2">
            Organization Chart
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Showing up to 5 levels. Click <AddIcon sx={{ fontSize: 12, verticalAlign: 'middle' }} /> on any node to expand further.
          </Typography>
        </Box>
        <Box sx={{ height: 'calc(100vh - 200px)', width: '100%' }}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            fitView
            fitViewOptions={{ padding: 0.2 }}
            minZoom={0.1}
            maxZoom={1.5}
            nodesDraggable={false}
            nodesConnectable={false}
            elementsSelectable={false}
          >
            <Background color="#f0f0f0" gap={20} />
            <Controls showInteractive={false} />
          </ReactFlow>
        </Box>
      </Card>
    </OrgChartContext.Provider>
  );
}
